import { Capacitor } from '@capacitor/core'

import { useApiStatus } from './use-api-status'

import { AuthLoginModal } from '#components'
import { appVersion } from '~~/shared/version'

export function useApi() {
  const config = useRuntimeConfig()
  const apiStatus = useApiStatus()
  const modal = useModal()
  const controller = new AbortController()
  const { $network, $log } = useNuxtApp()

  function getCustomHeaders() {
    return {
      'Guest-Id': $log?.getGuestId(),
      'App-Platform': Capacitor.getPlatform(),
      'App-Version': appVersion,
    }
  }

  const api = async <T>(url: string, options?: any): Promise<T> => {
    const response = await $fetch(url,
      {
        baseURL: config.public.platformApiBaseUrl,
        signal: controller.signal,
        credentials: 'include',
        retry: 1,
        retryStatusCodes: [401],
        ...options,

        async onRequest({ options }) {
          if ($network?.isMaintenanceModeEnabled.value) {
            console.log('Cancelling API call due to maintenance mode')
            controller.abort()
          }

          options.headers = {
            ...options.headers,
            ...getCustomHeaders(),
            ...useRequestHeaders(['cookie']),
          }

          apiStatus.value.isLoading = true
          apiStatus.value.response.error = null
          apiStatus.value.response.message = null
          apiStatus.value.response.status = null

          // Check to see if we are online or in maintenance mode
          $network.checkConnection()
        },

        onResponse() {
          apiStatus.value.isLoading = false
        },

        async onResponseError({ response }) {
          const { $log } = useNuxtApp()
          const message = response._data?.message || response._data?.statusMessage || response.statusText
          const responseCode = response.status

          if (responseCode === 401) {
            $log.debug('Auth session expired, showing login modal')

            apiStatus.value.isLoading = false

            return modal.open(AuthLoginModal, {
              sessionExpired: true,
            })
          }

          apiStatus.value.response.message = message

          apiStatus.value.response.error = message

          if (responseCode >= 500) {
            $log.error(`API error: ${response.url}`, message)
          }

          apiStatus.value.isLoading = false
        },
      })

    return response as T
  }

  return {
    api,
    apiStatus,
    getCustomHeaders,
  }
}
